import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getCompany, createCompany, upsertService, upsertIdentityConfig } from '../../lib/api';
import { CheckCircle } from 'lucide-react';

const IdentityOnboarding = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasCompany, setHasCompany] = useState(false);
  const [formData, setFormData] = useState({
    tier: '', companyName: '', companySize: '', platform: ''
  });
  const { user } = useAuth();
  const navigate = useNavigate();

  // Check if user already has a company
  useEffect(() => {
    if (!user) return;
    getCompany().then(({ company }) => {
      if (company) {
        setHasCompany(true);
        setFormData(prev => ({ ...prev, companyName: company.name || '', companySize: company.size || '' }));
      }
    }).catch(() => {});
  }, [user]);

  const handleTierSelect = async (tier) => {
    setFormData(prev => ({ ...prev, tier }));
    setLoading(true);
    try {
      const { company } = await getCompany();
      if (company) {
        setHasCompany(true);
        setFormData(prev => ({ ...prev, tier, companyName: company.name || '', companySize: company.size || '' }));
        setStep(3); // Skip company info, go to platform selection
      } else {
        setStep(2); // Need company info
      }
    } catch {
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createCompany({ name: formData.companyName, size: formData.companySize });
      setHasCompany(true);
      setStep(3);
    } catch (err) {
      console.error('Error creating company:', err);
      alert('Failed to create company: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePlatformSelect = (platform) => {
    setFormData(prev => ({ ...prev, platform }));
    setStep(4);
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Create/update service subscription
      await upsertService({
        service_type: 'IDENTITY_SERVICES',
        tier: formData.tier,
        onboarding_status: 'COMPLETED'
      });

      // Save identity config
      await upsertIdentityConfig({
        platform: formData.platform,
        config_data: { tier: formData.tier, companyName: formData.companyName, companySize: formData.companySize }
      });

      navigate('/dashboard/identity');
    } catch (err) {
      console.error('Error:', err);
      navigate('/dashboard/identity');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '4rem 1rem' }}>
      {/* Progress Bar */}
      <div style={{ marginBottom: '3rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
          {[1, 2, 3, 4].map((s) => (
            <div key={s} style={{ flex: 1, height: '4px', background: s <= step ? 'var(--blue)' : 'var(--gray)', marginRight: s < 4 ? '0.5rem' : 0, borderRadius: '2px' }} />
          ))}
        </div>
        <p style={{ color: 'var(--gray)', fontSize: '0.875rem' }}>Step {step} of 4</p>
      </div>

      {/* Step 1: Select Tier */}
      {step === 1 && (
        <div>
          <h1 className="hero-title" style={{ fontSize: '2rem', marginBottom: '1rem' }}>Choose Your Service Tier</h1>
          <p style={{ color: 'var(--gray)', marginBottom: '2rem' }}>Select the IAM implementation package that fits your organization</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[
              { name: 'up_to_50', price: 'Per user/month', users: 'Up to 50 users', desc: 'First IAM implementation — SSO, MFA, quarterly reviews', recommended: false },
              { name: 'standard', price: 'Per user/month', users: '50+ users', desc: 'Scale & compliance-ready — advanced workflows, SOC 2, priority support', recommended: true }
            ].map((plan) => (
              <div key={plan.name} className={plan.recommended ? "card-emphasis" : "card"}
                onClick={() => !loading && handleTierSelect(plan.name)}
                style={{ cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1, position: 'relative', background: plan.recommended ? 'rgba(59, 130, 246, 0.05)' : 'var(--white)' }}>
                {plan.recommended && (
                  <div style={{ position: 'absolute', top: '-12px', right: '20px', background: 'var(--blue)', color: 'var(--white)', padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700 }}>RECOMMENDED</div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div>
                    <h3 className="subsection-title" style={{ textTransform: 'uppercase' }}>{plan.name}</h3>
                    <p style={{ fontSize: '0.875rem', color: 'var(--gray)', marginBottom: '0.5rem' }}>{plan.users}</p>
                    <p style={{ fontSize: '0.875rem', color: 'var(--charcoal)' }}>{plan.desc}</p>
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, textAlign: 'right' }}>{plan.price}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Company Info */}
      {step === 2 && (
        <div>
          <h1 className="hero-title" style={{ fontSize: '2rem', marginBottom: '1rem' }}>Company Information</h1>
          <p style={{ color: 'var(--gray)', marginBottom: '2rem' }}>Tell us about your organization</p>
          <form onSubmit={handleCompanySubmit}>
            <div className="card-emphasis">
              <div style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">Company Name</label>
                <input type="text" value={formData.companyName} onChange={(e) => setFormData({ ...formData, companyName: e.target.value })} required className="form-input" placeholder="Acme Corp" />
              </div>
              <div style={{ marginBottom: '2rem' }}>
                <label className="form-label">Company Size</label>
                <select value={formData.companySize} onChange={(e) => setFormData({ ...formData, companySize: e.target.value })} required className="form-input">
                  <option value="">Select size</option>
                  <option value="10-25">10-25 employees</option>
                  <option value="25-50">25-50 employees</option>
                  <option value="50-100">50-100 employees</option>
                  <option value="100+">100+ employees</option>
                </select>
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
                {loading ? 'Setting up...' : 'Continue'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Step 3: Platform Selection */}
      {step === 3 && (
        <div>
          <h1 className="hero-title" style={{ fontSize: '2rem', marginBottom: '1rem' }}>Select IAM Platform</h1>
          <p style={{ color: 'var(--gray)', marginBottom: '2rem' }}>Choose the identity platform we'll implement</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[
              { name: 'okta', desc: 'Industry-leading SSO and MFA', price: '~$6–$18/user/mo', best: 'Best for 50-500 employees' },
              { name: 'entra', desc: 'Microsoft Entra ID (Azure AD)', price: '~$6–$12/user/mo', best: 'Best for M365 customers' },
              { name: 'miniorange', desc: 'Budget-friendly option', price: '~$2–$6/user/mo', best: 'Best for startups' }
            ].map((platform) => (
              <div key={platform.name} className="card" onClick={() => handlePlatformSelect(platform.name)} style={{ cursor: 'pointer' }}>
                <h3 className="subsection-title" style={{ textTransform: 'uppercase' }}>{platform.name}</h3>
                <p style={{ color: 'var(--charcoal)', marginBottom: '0.5rem' }}>{platform.desc}</p>
                <p style={{ fontSize: '0.875rem', color: 'var(--gray)', marginBottom: '0.25rem' }}>{platform.price}</p>
                <p style={{ fontSize: '0.875rem', color: 'var(--blue)', fontWeight: 600 }}>{platform.best}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 4: Complete */}
      {step === 4 && (
        <div>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div style={{ width: '80px', height: '80px', background: '#dbeafe', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 2rem' }}>
              <CheckCircle size={48} color="#1e40af" />
            </div>
            <h1 className="hero-title" style={{ fontSize: '2rem', marginBottom: '1rem' }}>Ready to Begin!</h1>
            <p style={{ color: 'var(--gray)', fontSize: '1.1rem' }}>Your identity services implementation is scheduled</p>
          </div>
          <div className="card-emphasis" style={{ marginBottom: '2rem' }}>
            <h3 className="subsection-title" style={{ marginBottom: '1rem' }}>What Happens Next?</h3>
            <ul style={{ paddingLeft: '1.5rem', color: 'var(--charcoal)' }}>
              <li>Our team will contact you within 24 hours</li>
              <li>We'll schedule a kickoff call to discuss requirements</li>
              <li>Implementation will begin within 1-2 weeks</li>
              <li>You can track progress in your dashboard</li>
            </ul>
          </div>
          <button onClick={handleComplete} className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
            {loading ? 'Saving...' : 'Go to Dashboard'}
          </button>
        </div>
      )}
    </div>
  );
};

export default IdentityOnboarding;
